import { useState } from "react"

import { DnoResponses } from "../../../../../constants/enums"
import { parseErrorAsObj } from "../../../../../errors/errorProcessors"
import { IAppError } from "../../../../../errors/errorTypes"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import { RESPOND_TO_APPLICATION } from "../../../urls/hybrid"

interface IDnoResponseHook {
    isSending: boolean
    error: IAppError | undefined
    postResponse: (
        applicationId: string,
        dnoResponse: DnoResponses,
        generationLimit: string | null,
        demandLimit: string | null,
        generationLimitReasons: string[] | null,
        demandLimitReasons: string[] | null,
        dnoWorksRequiredReasons: string[] | null,
        comments: string,
        onSuccessfulSubmit: () => void
    ) => Promise<void>
}

/**
 * Provides functions and loigc for posting a DNO response
 * to an escalated LCT application.
 * @returns {IDnoResponseHook} - loading and error states.
 */

const useDnoResponse = (): IDnoResponseHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postResponse = async (
        applicationId: string,
        dnoResponse: DnoResponses,
        generationLimit: string | null,
        demandLimit: string | null,
        generationLimitReasons: string[] | null,
        demandLimitReasons: string[] | null,
        dnoWorksRequiredReasons: string[] | null,
        comments: string,
        onSuccessfulSubmit: () => void
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response
            let body
            if (dnoResponse === DnoResponses.Approve) {
                body = {
                    applicationId,
                    action: dnoResponse,
                    generationLimit: null,
                    demandLimit: null,
                    demandLimitReasons: null,
                    generationLimitReasons: null,
                    comment: comments,
                }
            } else if (dnoResponse === DnoResponses.ConditionalApprove) {
                body = {
                    applicationId,
                    action: dnoResponse,
                    generationLimit: generationLimit ?? null,
                    demandLimit: demandLimit ?? null,
                    demandLimitReasons: demandLimitReasons ?? null,
                    generationLimitReasons: generationLimitReasons ?? null,
                    comment: comments,
                }
            } else if (dnoResponse === DnoResponses.furtherInformation) {
                body = {
                    applicationId,
                    action: dnoResponse,
                    generationLimit: null,
                    demandLimit: null,
                    demandLimitReasons: null,
                    generationLimitReasons: null,
                    comment: comments,
                }
            } else if (dnoResponse === DnoResponses.DnoWorksRequired) {
                body = {
                    applicationId,
                    action: dnoResponse,
                    generationLimit: null,
                    demandLimit: null,
                    demandLimitReasons: null,
                    generationLimitReasons: null,
                    dnoWorksRequiredReasons: dnoWorksRequiredReasons ?? null,
                    comment: comments,
                }
            } else {
                throw new Error("Invalid DNO response")
            }
            await postClientProtected(RESPOND_TO_APPLICATION, body)
            snackbar("Response submitted successfully", "success")
            setError(undefined)
            onSuccessfulSubmit()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postResponse,
    }
}

export default useDnoResponse
